// File an inbound fax to a client by hand — for faxes the automatic match
// wasn't sure about, or to move one that landed on the wrong child. Calls
// assign-fax, which moves and renames the Drive file and ticks any open
// request item the document answers.
import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { assignFax, DOC_TYPE_LABEL, DOC_TYPES } from '../../lib/faxes';

export default function AssignFaxModal({ fax, onClose, onDone }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [docType, setDocType] = useState(fax.doc_type || 'other');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Seed the search with the automatic best guess, if there was one.
  useEffect(() => {
    const guess = fax.extracted?.best_guess;
    const name = [fax.extracted?.patient_last_name, fax.extracted?.patient_first_name].filter(Boolean).join(' ');
    if (guess?.code) setQ(guess.code);
    else if (name) setQ(name);
  }, [fax]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      const words = term.split(/\s+/).filter(Boolean);
      let query = supabase.from('clients').select('id, client_id, first_name, last_name, date_of_birth, medicaid_id, current_status').limit(12);
      if (/^g\d+/i.test(term)) query = query.ilike('client_id', `${term}%`);
      else if (words.length >= 2) query = query.or(`and(last_name.ilike.${words[0]}%,first_name.ilike.${words[1]}%),and(first_name.ilike.${words[0]}%,last_name.ilike.${words[1]}%)`);
      else query = query.or(`last_name.ilike.%${term}%,first_name.ilike.%${term}%,medicaid_id.ilike.${term}%`);
      const { data } = await query.order('last_name');
      setResults(data || []);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = async () => {
    if (!picked) return;
    setBusy(true); setErr('');
    try {
      const res = await assignFax(fax.id, picked.id, docType);
      onDone?.(res);
      onClose();
    } catch (e) { setErr(e.message || 'Could not file this fax.'); }
    finally { setBusy(false); }
  };

  const ex = fax.extracted || {};
  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" onMouseDown={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">File this fax to a client</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: 'var(--text2)' }}>
            <div><b>{DOC_TYPE_LABEL[fax.doc_type] || 'Fax'}</b>{fax.sender_name ? ` from ${fax.sender_name}` : ''}{fax.pages ? ` · ${fax.pages} pages` : ''}</div>
            {fax.summary && <div style={{ marginTop: 4 }}>{fax.summary}</div>}
            {(ex.patient_last_name || ex.patient_first_name) && (
              <div style={{ marginTop: 4, color: 'var(--text3)' }}>Read as: {[ex.patient_last_name, ex.patient_first_name].filter(Boolean).join(', ')}{ex.patient_dob ? ` · DOB ${ex.patient_dob}` : ''}{ex.medicaid_id ? ` · Medicaid ${ex.medicaid_id}` : ''}</div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Client <span className="req">*</span></label>
            <input className="form-input" value={q} onChange={e => { setQ(e.target.value); setPicked(null); }} placeholder="Last name, first name, G-number or Medicaid ID" autoFocus />
          </div>
          {!picked && results.length > 0 && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 12, maxHeight: 220, overflowY: 'auto' }}>
              {results.map(c => (
                <button key={c.id} type="button" onClick={() => setPicked(c)}
                  style={{ display: 'flex', width: '100%', textAlign: 'left', gap: 10, alignItems: 'center', padding: '8px 12px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer', color: 'var(--text)', fontSize: 13 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 11.5, color: 'var(--text3)', minWidth: 52 }}>{c.client_id}</span>
                  <span style={{ fontWeight: 600 }}>{c.last_name}, {c.first_name}</span>
                  <span style={{ color: 'var(--text3)', fontSize: 12 }}>{c.date_of_birth || ''}{c.current_status ? ` · ${c.current_status}` : ''}</span>
                </button>
              ))}
            </div>
          )}
          {picked && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 11.5, color: 'var(--accent)' }}>{picked.client_id}</span>
              <b>{picked.last_name}, {picked.first_name}</b>
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setPicked(null)}>Change</button>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Document type</label>
            <select className="form-select" value={docType} onChange={e => setDocType(e.target.value)}>
              {DOC_TYPES.map(t => <option key={t} value={t}>{DOC_TYPE_LABEL[t]}</option>)}
            </select>
          </div>
          {err && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={go} disabled={busy || !picked}>{busy ? 'Filing…' : 'File to client'}</button>
        </div>
      </div>
    </div>
  );
}
